import { FormQuestionOptions } from "domain/entities/form-question-options";
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "primereact/button";
import QuestionStepper, { QuestionStepperRef } from "presentation/components/NimberForms/RespondToForm/QuestionStepper";
import QuestionStepperPanel from "presentation/components/NimberForms/RespondToForm/QuestionStepper/QuestionStepperPanel";
import { InputText } from "primereact/inputtext";
import { Dropdown } from "primereact/dropdown";
import { useGetInstanceByFilter } from "presentation/hooks/use-query-formsinstance";
import { FormInstanceInputFilter, QuestionInputFilter } from "domain/models/nimberforms-input-filters";
import { FormInstance } from "domain/entities/form-instance";
import { Dialog } from "primereact/dialog";
import Spinner from "presentation/components/Shared/Spinner";
import { useGetQuestionByFilter } from "presentation/hooks/use-query-questions";
import { FormTemplate } from "domain/entities/form-template";
import { useCreateSubmissionMutation } from "presentation/hooks/use-query-submission";
import { Submission } from "domain/entities/submission";
import { Answer } from "domain/entities/answer";
import { useCreateAnswersMutation } from "presentation/hooks/use-query-answer";
import { Toolbar } from "primereact/toolbar";
import logo from "../../assets/logo.svg"
import { useFormTemplateById } from "presentation/hooks/use-query-formtemplate";
import { Toast } from "primereact/toast";
import { QuestionTypes } from "domain/enum/questionTypes";
import { InputTextarea } from "primereact/inputtextarea";
import { Checkbox } from "primereact/checkbox";
import { httpClient } from "infra/axios/axios-api";
import { Messages } from "primereact/messages";
import { Rating } from "primereact/rating";

const RespondToFormPage = () => {

    const {slug} = useParams();

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const boolOptions = ["Sim", "Não"];

    const questionOptions: FormQuestionOptions[] = [];

    const stepperRef = useRef<QuestionStepperRef>(null);
    const msgs = useRef<Messages>(null);
    const useSubmissionCreate = useCreateSubmissionMutation();
    const useAnswersCreate = useCreateAnswersMutation();

    const [textAnswers, setTextAnswers] = useState<Record<string, string>>({});
    const [boolAnswers, setBoolAnswers] = useState<Record<string, string>>({});
    const [multiAnswers, setMultiAnswers] = useState<Record<string, string>>({});
    const [emailAnswers, setEmailAnswers] = useState<Record<string, string>>({});
    const [phoneAnswers, setPhoneAnswers] = useState<Record<string, string>>({});
    const [showReview, setShowReview] = useState<boolean>(false);

    const [image, setImage] = useState<string | null>(null);
    const [ratingAnswers, setRatingAnswers] = useState<Record<string, number | null>>({});

    const [showSpinner, setShowSpinner] = useState<boolean>(false);
    const [instance, setInstance] = useState<FormInstance>({
        slug: "", 
        active: false, 
        templateId: "", 
        id: "",
        title: "",
        description: "",
        isAnonymous: false,
        logo: "",
        questionPerPage: 1
    });
    const [instanceIsValid, setInstanceIsValid] = useState<boolean | null>(null);
    const [submittedSuccess, setSubittedSuccess] = useState<boolean>(false);
    const [phoneNumber, setPhoneNumber] = useState<string>("");
    const [email, setEmail] = useState<string>("");
    const [acceptedRgpd, setAcceptedRgpd] = useState<boolean>(false);
    const [name, setName] = useState<string>("");

    const toast = useRef<Toast>(null);

    const {
        data: templateInstance,
        isLoading: isInstanceLoading,
        isError: isInstanceError,
        error: errorInstance,
        refetch: refetchInstance
    } = useGetInstanceByFilter({slug: slug} as FormInstanceInputFilter, {enabled: !!slug, retry: true});

    const templateId = (instance.templateId as FormTemplate)?.id;
    
    const {
        data: questions,
        isLoading: isQuestionsLoading,
        isError: isQuestionsError,
        error: errorQuestions,
        refetch: refetchQuestions
    } = useGetQuestionByFilter({formTemplateId: templateId} as QuestionInputFilter, {enabled: !!templateId});

    const {
        data: template,
        isLoading: isTemplateLoading,
        isError: isTemplateError,
        error: errorTemplate,
        refetch: refetchTemplate
    } = useFormTemplateById((instance.templateId as FormTemplate).id)

    const requiredRatingIds = useMemo(() => {
        return questions?.data?.filter(question =>
            question.type === QuestionTypes.RATING && question.required
        ).map(ratingQuestion => ratingQuestion.id) ?? [];
    }, [questions]);
    const fetchLogoUrl = async (logo: string) => {
        if (!logo || logo === "" ) return;
        try {
            
            const response = await httpClient.get(
            `/assets/${logo}`
            );
            
            setImage(response.data.url);
        
        } catch (error) {
            console.error("Erro ao buscar a logo:", error);
        }
    }
    
    useEffect(() => {
        if(!templateInstance && isInstanceLoading) return;
        if(templateInstance && templateInstance.data && templateInstance.data.length > 0 && templateInstance.data[0].active){
            setInstance(templateInstance.data[0]);
            setInstanceIsValid(true);
            refetchQuestions();

            fetchLogoUrl(templateInstance.data[0].logo);
        }
        else
            setInstanceIsValid(false);
            
    },[templateInstance, isInstanceLoading, errorInstance])

    useEffect(() =>{
        if(instance.templateId !== "")
        {
            refetchQuestions();
        }
    }, [instance])

    useEffect(() => {
        if(submittedSuccess){
            stepperRef.current?.setLastStep();
            msgs.current?.show({ severity: 'success', summary: 'Formulário submetido com sucesso!' });
        }
    }, [submittedSuccess])

    useEffect(() => {
        if (instanceIsValid === false) {
            const timer = setTimeout(() => {
                window.location.href = "https://nimber.pt";
            }, 2700);
            return () => clearTimeout(timer);
        }
    }, [instanceIsValid]);

    if(!templateInstance && isInstanceLoading) return <Spinner/>;
    if(isQuestionsLoading) return <Spinner />
    if(!template && isTemplateLoading) return <Spinner />

    const addRightControllerForType = (type: string, id: string, options: string[] = []) => {
        switch(type){
            case QuestionTypes.FREE_TEXT:
                return <InputTextarea 
                key={id} 
                value={textAnswers[id] ?? ""}
                style={{width: "60%"}} 
                onChange={(e) => { setTextAnswers(prev => ({...prev, [id]: e.target.value}))}} />
            case QuestionTypes.BOOLEAN:
                return (<Dropdown
                        value={boolAnswers[id]}
                        placeholder="Escolha um"
                        options={boolOptions}
                        key={id}
                        style={{width: "60%"}}
                        onChange={(e) => {
                            setBoolAnswers(prev => ({...prev, [id]: e.target.value}))
                        }}
                        />)
            case QuestionTypes.MULTIPLE:
                return (<Dropdown
                        value={multiAnswers[id]}
                        placeholder="Escolha um"
                        options={options}
                        key={id}
                        style={{width: "60%"}}
                        onChange={(e) => {
                            setMultiAnswers(prev => ({...prev, [id]: e.target.value}))
                        }}
                        />)
            case QuestionTypes.EMAIL:
                return (<InputText
                        key={id} 
                        value={emailAnswers[id] ?? ""}
                        invalid={(emailAnswers[id] ?? "") !== "" &&!emailRegex.test(emailAnswers[id] ?? "")}
                        placeholder="nome@exemplo.com"
                        style={{width: "60%"}} 
                        onChange={(e) => { setEmailAnswers(prev => ({...prev, [id]: e.target.value}))}} />
            )
            case QuestionTypes.PHONE:
                return (<InputText
                        key={id} 
                        value={phoneAnswers[id] ?? ""}
                        placeholder="+351 912 345 678"
                        style={{width: "60%"}} 
                        onChange={(e) => { 
                            const cleaned = e.target.value.replace(/[^0-9+\-() ]/g, "");
                            setPhoneAnswers(prev => ({...prev, [id]: cleaned}))
                        }} />);
            case QuestionTypes.RATING:
                return (
                    <Rating 
                    stars={10}
                    value={ratingAnswers[id] ?? 0}
                    onChange={e => setRatingAnswers(prev => ({...prev, [id]: e.value ?? null}))}
                    />

                )
            
             
        }
    }

    const addIsRequired = (required: boolean) => {
        if(required)
            return (<span style={{color: "#ff0000"}}> *</span>)
        else
            return null;
    }

    const addDefaultToRequiredRatings = () =>{
        setRatingAnswers(prev => {
            const updated = { ...prev };
            for (const id of requiredRatingIds) {
                if (!updated[id]) updated[id] = 0;
            }
            return updated;
        });
    }

    const validatePersonalData = (): boolean => {
        return (name.trim() !== "" && emailRegex.test(email) && phoneNumber.trim() !== "" && acceptedRgpd);
    }
    const addPersonalInfoPanel = () => {
        if(instance.isAnonymous)
            return null;

        return (
            <QuestionStepperPanel>
                <div style={{ display: "flex", flexDirection: "column", alignItems: "center", width: "100%" }}>
                    <div style={{ width: "60%", paddingBottom: "1rem", paddingTop: "2rem" }}>
                        <h1>Dados Pessoais</h1>
                        <div className="p-fluid formgrid grid">

                            <div className="field col-12 md:col-6">
                                <label htmlFor="name">Nome <span style={{ color: "#ff0000" }}>*</span></label>
                                <InputText
                                    id="name"
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                />
                            </div>

                            <div className="field col-12 md:col-6">
                                <label htmlFor="personalEmail">Email <span style={{ color: "#ff0000" }}>*</span></label>
                                <InputText
                                    id="personalEmail"
                                    value={email}
                                    invalid={email !== "" && !emailRegex.test(email)}
                                    placeholder="nome@exemplo.com"
                                    onChange={(e) => setEmail(e.target.value)}
                                />
                            </div>

                            <div className="field col-12 md:col-6">
                                <label htmlFor="personalPhone">Número de Telefone <span style={{ color: "#ff0000" }}>*</span></label>
                                <InputText
                                    id="personalPhone"
                                    value={phoneNumber}
                                    placeholder="+351 912 345 678"
                                    onChange={(e) => {
                                        const cleaned = e.target.value.replace(/[^0-9+\-() ]/g, "");
                                        setPhoneNumber(cleaned);
                                    }}
                                />
                            </div>

                            <div className="field col-12 md:col-6 flex align-items-center gap-2" style={{ marginTop: "1.5rem" }}>
                                <Checkbox
                                    id="acceptedRgpd"
                                    checked={acceptedRgpd}
                                    onChange={(e) => setAcceptedRgpd(e.checked ?? false)}
                                />
                                <label htmlFor="acceptedRgpd" style={{ marginBottom: 0 }}>
                                    Li e aceito a <a href="https://www.nimber.pt/terms.pdf">Política de Privacidade</a>.
                                    <span style={{ color: "#ff0000" }}> *</span>
                                </label>
                            </div>

                        </div>
                    </div>

                    <div style={{ display: "flex", justifyContent: "end", width: "60%" }}>
                        <Button label="Próximo" icon="pi pi-arrow-right" iconPos="right" onClick={() => {
                            if(validatePersonalData())
                                stepperRef.current?.nextStep();
                            else
                                showToastError("Por favor preencha todos os campos com valores válidos");
                        }} />
                    </div>
                </div>
            </QuestionStepperPanel>
        );
    }

    const addQuestionsToPanel = (lastIndex: number) =>{
        const orderdQuestions = questions?.data?.sort((a, b) => a.order - b.order) ?? [];
        const questionsInPanel = [];
        for(let j = 0; j < instance.questionPerPage && (j + lastIndex) < orderdQuestions.length; j++){
            questionsInPanel.push((
                <div style={{
                    display: "flex", 
                    flexDirection: "column", 
                    justifyContent: "space-between", 
                    alignItems: "center", 
                    paddingBottom: "3rem",
                    width:"100%"}}>
                <div style={{width: "60%", display: "flex", justifyContent: "start", paddingBottom: "1rem", paddingTop: "2rem"}}>
                    <div style={{display: "flex", flexDirection: "row", alignItems: "center"}}>
                        <h1>{orderdQuestions[j+lastIndex].label}</h1>
                        {addIsRequired(orderdQuestions[j+lastIndex].required)}
                    </div>
                </div>
                {addRightControllerForType(orderdQuestions[j+lastIndex].type, orderdQuestions[j+lastIndex].id, orderdQuestions[j+lastIndex].questionOptions?.map((option) => option.value) ?? [])}
            </div>
            ));
        }
        return questionsInPanel;
    }

    const addStepButtons = (index: number) => {
        return  (index === 0 && instance.isAnonymous) ? (
            <div style={{display: "flex", justifyContent: "end", width: "80%"}}>
                <Button label="Próximo" icon="pi pi-arrow-right" iconPos="right" onClick={() => stepperRef.current?.nextStep()} />
            </div>
        ) : (
            <div style={{display: "flex", justifyContent: "space-between", width: "80%"}}>        
                    <Button label="Anterior" icon="pi pi-arrow-left" severity="secondary" iconPos="right" onClick={() => stepperRef.current?.prevStep()} />
                    <Button label="Próximo" icon="pi pi-arrow-right" iconPos="right" onClick={() => stepperRef.current?.nextStep()} />
            </div>
        )
    }
    const addQuestionPanels = () => {
        const ammountOfQuestionPanels = Math.ceil((questions?.data?.length ?? 0) / instance.questionPerPage);

        const panels = [];
        let lastIndex = 0;
        for(let i = 0; i < ammountOfQuestionPanels; i++){ 

            panels.push(
                <QuestionStepperPanel>
                    {addQuestionsToPanel(lastIndex)}
                    {addStepButtons(i)}
                </QuestionStepperPanel>
            
            );
            lastIndex += instance.questionPerPage;
        }

        return panels;
    }

    const showToastError = (detail: string) => {
        toast.current?.show({ severity: "error", summary: "Erro", detail, life: 1500 });
    };

    const populateSubmitPayload = (submissionId: string, payload: Answer[]) => {
        addAnswersToPayload(textAnswers, payload, submissionId);

        addAnswersToPayload(boolAnswers, payload, submissionId);

        addAnswersToPayload(multiAnswers, payload, submissionId);

        addAnswersToPayload(emailAnswers, payload, submissionId);

        addAnswersToPayload(phoneAnswers, payload, submissionId);
    }

    const areQuestionsValid = (): boolean => {
        if(!isRequiredQuestionsAnswered())
        {
            if(toast.current){
                toast.current?.show({
                    severity: "error",
                    summary: "Erro",
                    detail: "Responda a todas as perguntas obrigatórias",
                    life: 1500,
                });
            }
            return false;
        }

        if(!validateEmailQuestions()){
            if(toast.current){
                toast.current?.show({
                    severity: "error",
                    summary: "Erro",
                    detail: "Use um email válido",
                    life: 1500,
                });
            }
            return false;
        }
        return true;
    }

    const submitAnswers = async () => {
        if(!areQuestionsValid()) return;

        setShowSpinner(true);

        const submission: Submission = {instanceId: instance.id as string, id: "", acceptedRgpd: false}
        if(!instance.isAnonymous){
            submission.acceptedRgpd = acceptedRgpd;
            submission.name = name;
            submission.email = email;
            submission.numberPhone = phoneNumber;
        }
        addDefaultToRequiredRatings();

        const returnedSubmission = await useSubmissionCreate.mutateAsync(submission)
        const submissionID = returnedSubmission.data?.id as string;
        const answerPayloads: Answer[] = [];
       
        populateSubmitPayload(submissionID, answerPayloads);

        Object.entries(ratingAnswers).forEach(([key, value]) => {
            const answerPayload: Answer = { value: value?.toString() ?? "0", questionId: key, submissionId: submissionID};
            answerPayloads.push(answerPayload);
        })

        if(answerPayloads.length === 0){
            setShowSpinner(false);
            setSubittedSuccess(true);
            setTimeout(() => {
              window.location.href = "https://nimber.pt";
            }, 2000)
            return;
        }

        const answers = await useAnswersCreate.mutateAsync(answerPayloads);
        if(answers.data)
        {
            setShowSpinner(false);
            setSubittedSuccess(true);
            setTimeout(() => {
              window.location.href = "https://nimber.pt";
            }, 2000)
        }
    }

    const addAnswersToPayload = (answers: Record<string, string>, payload: Answer[], submissionId: string) => {
        Object.entries(answers).forEach(([key, value]) => {
            if (!value.trim()) return;
            const answerPayload: Answer = { value: value, questionId: key, submissionId: submissionId};
            payload.push(answerPayload);
        })
    }

    const validateEmailQuestions = (): boolean => {
        return Object.entries(emailAnswers).every(([key, value]) => {
            return emailRegex.test(value)
        
        });
    }

    const getRequiredQuestiosAnswered = (answeredQuestionIds: string[]) => {
        Object.entries(textAnswers).forEach(([key, value]) => {
            if(value && value.trim())
                answeredQuestionIds.push(key);
        });

        Object.entries(boolAnswers).forEach(([key, value]) => {
            if(value && value.trim())
                answeredQuestionIds.push(key);
        })

        Object.entries(multiAnswers).forEach(([key, value]) => {
            if(value && value.trim())
                answeredQuestionIds.push(key);
        })

        Object.entries(emailAnswers).forEach(([key, value]) => {
            if(value && value.trim())
                answeredQuestionIds.push(key);
        })

        Object.entries(phoneAnswers).forEach(([key, value]) => {
            if(value && value.trim())
                answeredQuestionIds.push(key);
        })
    }
    
    const isRequiredQuestionsAnswered = () => {
        const requiredQuestions = getRequiredQuestions();
        
        const answeredQuestionIds: string[] = [];
        getRequiredQuestiosAnswered(answeredQuestionIds);

        for(const requiredId of requiredQuestions){
            if(requiredRatingIds.includes(requiredId) )
                continue;
            if(!answeredQuestionIds.includes(requiredId))
                return false;
        }
        return true;
    }

    const getRightControllerForReview = (questionId: string,type: string, answer: string | number) => {
        switch(type){
            case QuestionTypes.BOOLEAN:
                return (<Dropdown
                        value={answer}
                        key={questionId}
                        style={{width: "60%"}}
                        options={[answer]}
                        disabled
                        />)
            case QuestionTypes.MULTIPLE:
                return (<Dropdown
                        value={answer}
                        key={questionId}
                        style={{width: "60%"}}
                        options={[answer]}
                        disabled
                        />);
            case QuestionTypes.EMAIL:
                return (<InputText
                        key={questionId}
                        value={answer as string}
                        style={{width: "60%"}} 
                        disabled />);
            case QuestionTypes.PHONE:
                return (<InputText
                        key={questionId}
                        value={answer as string}
                        style={{width: "60%"}} 
                        disabled />);
            case QuestionTypes.RATING:
                let ansValue = 0;
                if(typeof answer === "number")
                    ansValue = answer;
                return (
                    <Rating 
                    stars={10}
                    value={ansValue}
                    disabled
                    />);
            case QuestionTypes.FREE_TEXT:
                return <InputTextarea 
                key={questionId} 
                value={answer as string}
                style={{width: "60%"}} 
                disabled
                />
            default:
                return <InputText value={answer as string} disabled />;
        }

    }
    const showAnswersForReview = () => {
        const withoutAnonymous = instance.isAnonymous ? 0 : 1;
        return questions?.data?.map((question, index) => {
            const answer = getAnswerForType(question.id, question.type);
            return (
                 <div style={{
                    display: "flex", 
                    flexDirection: "column", 
                    justifyContent: "space-between", 
                    alignItems: "center", 
                    paddingBottom: "3rem",
                    width:"100%"}}>
                <div style={{width: "60%", display: "flex", justifyContent: "space-between", paddingBottom: "1rem", paddingTop: "2rem"}}>
                    <h1>{question.label}</h1>
                    <Button 
                    text
                    icon="pi pi-pencil"
                    onClick={() =>{ stepperRef.current?.setStep(index + withoutAnonymous)}}
                    />
                </div>
                {getRightControllerForReview(question.id, question.type, answer)}
                
            </div>
            );
        });
    }

    const getAnswerForType = (questionId: string, type: string) => {
        switch(type){
            case QuestionTypes.FREE_TEXT:
                return textAnswers[questionId]?.trim() === "" ? "Sem resposta" : textAnswers[questionId] ?? "Sem resposta";
            case QuestionTypes.BOOLEAN:
                return boolAnswers[questionId]?.trim() === "" ? "Sem resposta" : boolAnswers[questionId] ?? "Sem resposta";
            case QuestionTypes.MULTIPLE:
                return multiAnswers[questionId]?.trim() === "" ? "Sem resposta" : multiAnswers[questionId] ?? "Sem resposta";
            case QuestionTypes.EMAIL:
                return emailAnswers[questionId]?.trim() === "" ? "Sem resposta" : emailAnswers[questionId] ?? "Sem resposta";
            case QuestionTypes.PHONE:
                return phoneAnswers[questionId]?.trim() === "" ? "Sem resposta" : phoneAnswers[questionId] ?? "Sem resposta";
            case QuestionTypes.RATING:
                return ratingAnswers[questionId] ?? "Sem resposta";
            default:
                return "Sem resposta";
        }
    }

    const getRequiredQuestions = () => {
        return questions?.data
        ?.filter((question) => question.required)
        ?.map((question) => question.id) ?? [];
    }

    if(!instanceIsValid)
    return (
        <Dialog visible={true} header="Erro" onHide={() => {}} >
            <h1>O formulário não foi encontrado.</h1>
            <h2>Redirecionando para o site da nimber...</h2>
        </Dialog>
    )
    
    const logoAnchor = (
        <a href="https://nimber.pt">
            <img src={logo} alt="Nimber Logo" className="logo" />
        </a>
    )

    if(showSpinner)
        return <Spinner />

    return (
        <>
        <Toolbar start={logoAnchor} />
        <div 
        style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            flexDirection: "column",
            width: "100vw",
        }}>
            <QuestionStepper title={instance.title} style={{
                borderRadius: "25px",
                border: "2px dashed black",
                width: "75%",
                height: "90%",
                backgroundColor: "#faf7ef",
                marginTop: "1rem",
                marginBottom: "1rem"
                }} 
                ref={stepperRef}
                description={instance.description}
                logo={image}
                >
                {addPersonalInfoPanel()}
                {addQuestionPanels()}
                <QuestionStepperPanel>
                    {showReview && showAnswersForReview()}
                    <Button
                    label="Submeter"
                    icon="pi pi-check"
                    className="p-button-lg"
                    style={{marginTop: "7rem"}}
                    onClick={async () => {
                        await submitAnswers();
                    }}
                    />
                    <Button 
                    label="Rever respostas"
                    link
                    onClick={() => setShowReview(true)}
                    />
                    <div style={{display: "flex", justifyContent: "start", width: "80%"}}>
                        <Button label="Anterior" icon="pi pi-arrow-left" severity="secondary" iconPos="right" onClick={() => stepperRef.current?.prevStep()} />
                    </div>
                </QuestionStepperPanel>
            </QuestionStepper>
        </div>
        <div style={{ position: 'fixed', bottom: "3rem", left: "50%",transform: 'translateX(-50%)', zIndex: 10, width: "50vw"}}>
            <Messages ref={msgs} />
        </div>
        <Toast ref={toast} />
        </>
    );
}
export default RespondToFormPage